// Admin Dashboard — WP plugin parity (Screen 1 of admin).
// 4 metric tiles + Upcoming Holidays + Recent Notices + Today's Snapshot
// (Checked In / Out / WFH pills) + quick links.
//
// Phase 2 (Quantiv rebrand) is a restyle only: same /hr/admin/dashboard call,
// same counters, same lists, same destinations. What changed is the surface —
// the rainbow per-card colours and the hardcoded indigo portal button are gone,
// every colour now comes from a design token so it follows the theme, and the
// shared vocabulary (tiles, section heads, tables) moved to ./dashboardUi.

import React, { useEffect, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, EmptyState, Pill, Skeleton } from "../../Components/ui";
import {
  eyebrowStyle, emptyPad, MetricTile, SectionHead, PillLink,
  primaryPillStyle, tableStyle, Th, Td,
} from "./dashboardUi";

export default function AdminDashboard() {
  const toast = useToast();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useShellHeader({ title: "Quantiv Media HR — Dashboard", breadcrumb: "Admin overview" }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api("/hr/admin/dashboard");
        if (!cancel) setD(r);
      } catch (e) { if (!cancel) toast({ title: e.message, status: "error" }); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line
  }, []);

  if (loading || !d) {
    return <div style={{ padding: 16, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}><Skeleton.Card /><Skeleton.Card /><Skeleton.Card /><Skeleton.Card /></div>;
  }

  const { counters, upcomingHolidays, recentNotices, todaySnapshot } = d;
  const today = new Date();
  const todayLabel = today.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* ── Greeting header + pill actions ───────────────────────── */}
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
        <div>
          <div style={eyebrowStyle}>Admin overview · {todayLabel}</div>
          <h2 style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.025em", color: "var(--text-primary)", margin: "6px 0 0", lineHeight: 1.1 }}>
            Quantiv Media <span style={{ color: "var(--accent-text)" }}>HR</span>
          </h2>
        </div>

        {/* The three quick links used to sit in a footer strip under the
            snapshot card; same destinations, promoted to the header. */}
        <nav style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <PillLink to="/hr"         icon="👤" label="Manage Staff" />
          <PillLink to="/attendance" icon="🕐" label="Attendance" />
          <PillLink to="/leaves"     icon="📅" label="Leave Requests" />
          <a
            href="https://workspace.quantivmedia.com"
            target="_blank"
            rel="noopener noreferrer"
            title="Open the Quantiv Media workspace in a new tab"
            style={primaryPillStyle}
          >Go to Portal <span aria-hidden="true">↗</span></a>
        </nav>
      </header>

      {/* ── 4 metric tiles ──────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12 }}>
        <MetricTile icon="👤" tone="brand"   label="Active Staff"      value={counters.activeStaff} />
        <MetricTile icon="📅" tone="warning" label="Pending Leaves"    value={counters.pendingLeaves} />
        <MetricTile icon="💼" tone="info"    label="Active Projects"   value={counters.activeProjects} />
        <MetricTile icon="⭐" tone="violet"  label="Upcoming Holidays" value={counters.upcomingHolidays} />
      </div>

      {/* ── 2-col: Upcoming Holidays / Recent Notices ───────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
        <Card padding="none" style={{ overflow: "hidden" }}>
          <SectionHead icon="⭐" title="Upcoming Holidays" />
          {upcomingHolidays.length === 0 ? (
            <EmptyState description="None scheduled." style={emptyPad} />
          ) : (
            <table style={tableStyle}>
              <thead><tr><Th>Holiday</Th><Th>Date</Th></tr></thead>
              <tbody>
                {upcomingHolidays.map((h) => (
                  <tr key={h._id}>
                    <Td>{h.title}</Td>
                    <Td className="tabular" style={{ color: "var(--text-tertiary)" }}>{new Date(h.holidayDate).toISOString().slice(0, 10)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card padding="none" style={{ overflow: "hidden" }}>
          <SectionHead icon="📢" title="Recent Notices" />
          {recentNotices.length === 0 ? (
            <EmptyState description="No notices yet." style={emptyPad} />
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {recentNotices.map((n) => (
                <li key={n._id} style={{ padding: "12px 18px", borderTop: "1px solid var(--border-subtle)" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                    {n.pinned ? "📌 " : ""}{n.title}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 3 }}>
                    {new Date(n.createdAt).toLocaleDateString()} · {n.createdBy?.username || "—"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* ── Today's Attendance Snapshot ─────────────────────────── */}
      <Card padding="none" style={{ overflow: "hidden" }}>
        <SectionHead
          icon="📅"
          title="Today's Attendance Snapshot"
          subtitle={todayLabel}
          action={
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Pill tone="success" size="lg">✅ {todaySnapshot.checkedIn} Checked In</Pill>
              <Pill tone="danger"  size="lg">❌ {todaySnapshot.out} Out</Pill>
              <Pill tone="violet"  size="lg">🏠 {todaySnapshot.wfh} WFH</Pill>
            </div>
          }
        />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))" }}>
          <div style={{ padding: 18, borderRight: "1px solid var(--border-subtle)" }}>
            <div style={{ ...eyebrowStyle, color: "var(--tone-danger-fg)", marginBottom: 12 }}>❌ Out / on leave today</div>
            {todaySnapshot.outList.length === 0 ? (
              <EmptyState description="Everyone's in." style={emptyPad} />
            ) : (
              <table style={tableStyle}>
                <thead><tr><Th>Name</Th><Th>Department</Th><Th>Reason</Th></tr></thead>
                <tbody>
                  {todaySnapshot.outList.map((s, i) => (
                    <tr key={`out-${i}`}>
                      <Td>{(s.firstName || "") + " " + (s.lastName || "") || s.username}</Td>
                      <Td>{s.team?.name || s.team || "—"}</Td>
                      <Td>
                        {s.reason === "leave"
                          ? <Pill tone="warning">🏖 On leave</Pill>
                          : <Pill tone="danger">❌ Absent</Pill>}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div style={{ padding: 18 }}>
            <div style={{ ...eyebrowStyle, color: "var(--tone-violet-fg)", marginBottom: 12 }}>🏠 Working from home today</div>
            {todaySnapshot.wfhList.length === 0 ? (
              <EmptyState description="No one is WFH today." style={emptyPad} />
            ) : (
              <table style={tableStyle}>
                <thead><tr><Th>Name</Th><Th>Department</Th></tr></thead>
                <tbody>
                  {todaySnapshot.wfhList.map((s, i) => (
                    <tr key={`wfh-${i}`}>
                      <Td>{(s.firstName || "") + " " + (s.lastName || "") || s.username}</Td>
                      <Td>{s.team?.name || s.team || "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
